import React, { useState, useCallback, useEffect } from 'react';
import { api } from '../services/api.ts';
import * as itemizationApi from '../services/itemizationApi.ts';
import './ItemizationRules.css';

interface Category {
  id: number;
  name: string;
  icon: string;
  icon_color: string;
}

const emptyForm = (): itemizationApi.CreateRuleRequest => ({
  name: '',
  pattern: '',
  category_id: 0,
  priority: 0,
});

const ItemizationRules: React.FC = () => {
  const [rules, setRules] = useState<itemizationApi.ItemizationRule[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingRule, setEditingRule] = useState<itemizationApi.ItemizationRule | null>(null);
  const [form, setForm] = useState<itemizationApi.CreateRuleRequest>(emptyForm());
  const [saving, setSaving] = useState(false);

  // Preview
  const [previewInput, setPreviewInput] = useState('');
  const [previewResults, setPreviewResults] = useState<itemizationApi.ItemizationPreviewResult[]>([]);
  const [previewing, setPreviewing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesData, catsData] = await Promise.all([
        itemizationApi.getRules(),
        api.get<Category[]>('/api/v1/categories').then(r => r.data),
      ]);
      setRules(rulesData);
      setCategories(catsData);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.category_id) {
      setError('Selecione uma categoria');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, priority: editingRule ? editingRule.priority : rules.length };
      if (editingRule) {
        await itemizationApi.updateRule(editingRule.id, payload);
      } else {
        await itemizationApi.createRule(payload);
      }
      setShowForm(false);
      setEditingRule(null);
      setForm(emptyForm());
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Erro ao salvar regra');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (rule: itemizationApi.ItemizationRule) => {
    setEditingRule(rule);
    setForm({ name: rule.name, pattern: rule.pattern, category_id: rule.category_id, priority: rule.priority });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Excluir esta regra?')) return;
    try {
      await itemizationApi.deleteRule(id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Erro ao excluir regra');
    }
  };

  const handlePreview = async () => {
    if (!previewInput.trim()) return;
    const descriptions = previewInput.split('\n').map(s => s.trim()).filter(Boolean);
    setPreviewing(true);
    try {
      const results = await itemizationApi.previewRules(descriptions);
      setPreviewResults(results);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Erro ao testar regras');
    } finally {
      setPreviewing(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingRule(null);
    setForm(emptyForm());
    setError('');
  };

  if (loading) return <div className="itemization-rules"><div className="loading">Carregando...</div></div>;

  return (
    <div className="itemization-rules">
      <div className="page-header">
        <div>
          <h1>📋 Regras de Itemização</h1>
          <p>Configure padrões para categorizar automaticamente itens de faturas</p>
        </div>
        <button
          className="primary-button"
          onClick={() => {
            setEditingRule(null);
            setForm(emptyForm());
            setShowForm(!showForm);
          }}
        >
          {showForm && !editingRule ? 'Cancelar' : 'Nova Regra'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <div className="form-container">
          <h3>{editingRule ? 'Editar Regra' : 'Nova Regra'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Nome da Regra</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex: Netflix, Posto de Gasolina..."
                  required
                />
              </div>
              <div className="form-group">
                <label>Padrão (use * como curinga)</label>
                <input
                  type="text"
                  value={form.pattern}
                  onChange={e => setForm({ ...form, pattern: e.target.value })}
                  placeholder="Ex: NETFLIX*, *POSTO*"
                  required
                />
                <small>Use * para corresponder a qualquer texto</small>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Categoria</label>
                <select
                  value={form.category_id || ''}
                  onChange={e => setForm({ ...form, category_id: parseInt(e.target.value) || 0 })}
                  required
                >
                  <option value="">Selecione uma categoria</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-actions">
              <button type="submit" className="primary-button" disabled={saving}>
                {saving ? 'Salvando...' : (editingRule ? 'Salvar Alterações' : 'Criar Regra')}
              </button>
              <button type="button" className="secondary-button" onClick={handleCancel} disabled={saving}>
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Preview section */}
      <div className="preview-section">
        <h3>Testar Regras</h3>
        <div className="preview-input-row">
          <input
            type="text"
            value={previewInput}
            onChange={e => setPreviewInput(e.target.value)}
            placeholder="Digite descrições separadas por linha..."
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handlePreview()}
          />
          <button className="primary-button" onClick={handlePreview} disabled={previewing}>
            {previewing ? '...' : 'Testar'}
          </button>
        </div>
        {previewResults.length > 0 && (
          <div className="preview-results">
            {previewResults.map((r, i) => (
              <div key={i} className={`preview-result-item ${r.matched_rule_id ? 'matched' : 'unmatched'}`}>
                <span className="preview-desc">{r.description}</span>
                {r.matched_rule_id
                  ? <span className="preview-rule-name">✓ {r.matched_rule_name}</span>
                  : <span className="preview-no-match">✗ Sem correspondência</span>
                }
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Rules list */}
      {rules.length === 0 ? (
        <div className="empty-state">
          <p>Nenhuma regra cadastrada.</p>
          <p>Crie regras para categorizar automaticamente itens ao importar faturas.</p>
        </div>
      ) : (
        <div className="rules-list">
          {rules.map((rule, index) => (
            <div key={rule.id} className="rule-card">
              <div className="rule-priority">{index + 1}</div>
              <div className="rule-info">
                <div className="rule-name">{rule.name}</div>
                <div className="rule-pattern">{rule.pattern}</div>
              </div>
              {rule.category && (
                <span className="rule-category">
                  {rule.category.icon} {rule.category.name}
                </span>
              )}
              <div className="rule-actions">
                <button className="edit-button" onClick={() => handleEdit(rule)} title="Editar">
                  <img src="/edit.svg" alt="Editar" className="icon" />
                </button>
                <button className="delete-button" onClick={() => handleDelete(rule.id)} title="Excluir">
                  <img src="/delete.svg" alt="Excluir" className="icon" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ItemizationRules;
